/**
 * Playback Queue Store
 * Manages frontend-controlled playback queue for normal (non-hookmode) playback
 */

import { create } from 'zustand';

export type QueueSource = 'single' | 'selection' | 'playlist' | 'from-track';

export interface QueueItem {
  uri: string;
  id: string;
  durationMs: number;
}

export interface PlaybackQueueState {
  items: QueueItem[];
  currentIndex: number;
  source: QueueSource;
  previewMode: 'full' | 'hook';
  hookTimerId: NodeJS.Timeout | null;
  // True when playback was started with contextUri (Spotify manages queue)
  isContextMode: boolean;

  // Actions
  setQueue: (items: QueueItem[], startIndex: number, source: QueueSource, isContextMode?: boolean) => void;
  movePrevious: () => QueueItem | null;
  moveNext: () => QueueItem | null;
  getCurrentItem: () => QueueItem | null;
  getQueuedIds: () => Set<string>;
  clear: () => void;
  setPreviewMode: (mode: 'full' | 'hook') => void;
  setHookTimer: (id: NodeJS.Timeout | null) => void;
  clearHookTimer: () => void;
  togglePreviewMode: () => void;
}

export const usePlaybackQueueStore = create<PlaybackQueueState>((set, get) => ({
  items: [],
  currentIndex: 0,
  source: 'single',
  previewMode: 'full' as const,
  hookTimerId: null,
  isContextMode: false,

  setQueue: (items, startIndex, source, isContextMode = false) => {
    set({ items, currentIndex: startIndex, source, isContextMode });
  },

  movePrevious: () => {
    const { items, currentIndex } = get();
    if (currentIndex > 0) {
      const newIndex = currentIndex - 1;
      set({ currentIndex: newIndex });
      return items[newIndex] ?? null;
    }
    return null;
  },

  moveNext: () => {
    const { items, currentIndex } = get();
    if (currentIndex < items.length - 1) {
      const newIndex = currentIndex + 1;
      set({ currentIndex: newIndex });
      return items[newIndex] ?? null;
    }
    return null;
  },

  getCurrentItem: () => {
    const { items, currentIndex } = get();
    return items[currentIndex] ?? null;
  },

  getQueuedIds: () => {
    const { items, currentIndex } = get();
    // Return IDs of tracks that will be played (current + remaining)
    const queuedItems = items.slice(currentIndex);
    return new Set(queuedItems.map(item => item.id));
  },

  clear: () => {
    set({ items: [], currentIndex: 0, source: 'single', isContextMode: false });
  },

  setPreviewMode: (mode) => {
    set({ previewMode: mode });
  },

  setHookTimer: (id) => {
    // Clear existing timer before setting new one
    const currentTimer = get().hookTimerId;
    if (currentTimer) {
      clearTimeout(currentTimer);
    }
    set({ hookTimerId: id });
  },

  clearHookTimer: () => {
    const timerId = get().hookTimerId;
    if (timerId) {
      clearTimeout(timerId);
      set({ hookTimerId: null });
    }
  },

  togglePreviewMode: () => {
    const current = get().previewMode;
    set({ previewMode: current === 'full' ? 'hook' : 'full' });
  },
}));
